// Situation grouping for the live feed — presentation only, no storage change.
//
// The backend dedupes REPORTS (same claim, reworded) into one event; this
// module groups EVENTS (a developing story) into one card. One strike wave
// arrives as unclear -> explosion -> impact -> airstrike across an hour, and
// each kind step is honestly a different event — but reading them as six feed
// rows buries what is actually going on. A situation is "activity at a place
// while reports keep coming": events connect when they share an attached
// place entity, a normalized place name, or (for pinpoint reports) sit within
// 10 km — and a quiet gap splits the timeline, so tonight's second wave of
// strikes on the same base is a new card, not a bump to this afternoon's.
//
// Grouping stays client-side and recomputable on purpose: an over-merge here
// hides nothing (expand the card and every report is intact), where an
// over-merge in the backend dedupe rewrites version history.

import type { EntityDto } from './api';
import { lastReportOf, whenOf } from './when';

export interface Situation {
  key: string;            // oldest member event's id — stable across re-renders,
                          // so UI state (an expanded card) can survive a refresh
  place: string;          // display name for the card header
  events: EntityDto[];    // newest first
  firstAt: number;        // ms — earliest report in the session
  lastAt: number;         // ms — latest activity (merged reports included)
  kinds: string[];        // unique kinds, in order of first appearance
  channels: string[];     // distinct source channels across all folded reports
  reportCount: number;    // total source posts behind the card
}

/** Quiet time that closes a situation. MUST MATCH the server matcher's
 *  JOIN_QUIET_H (app/incidents/matcher.py): this module is the fallback for
 *  whatever the router has not placed yet, and a client that splits at 90 min
 *  while the server joins across 6 h renders two cards for the same reports
 *  depending on which side grouped them — exactly the inconsistency the
 *  deterministic matcher exists to end. Widened from 90 min with the product
 *  decision (2026-07-27) that one place under a night of waves is ONE card;
 *  a report after the gap still starts a NEW card at the same place. */
export const SESSION_GAP_MS = 6 * 60 * 60_000;

/** Pinpoint reports of one thing scatter by geocode wobble ("Suza"/"Sooza"/
 *  "Souza" resolve metres to kilometres apart). Matches the backend's
 *  PLACE_ATTACH_M so "nearby" means the same thing on both sides. */
export const NEAR_M = 10_000;

// --- place-name normalization (light port of app/live/dedupe.normalize_place)

const QUALIFIER_RE = /^(?:the\s+)?(?:(?:far\s+)?(?:north|south|east|west|north[- ]?east|north[- ]?west|south[- ]?east|south[- ]?west)\s+of\s+|near(?:by)?\s+|around\s+|close\s+to\s+|just\s+outside\s+|(?:the\s+)?(?:outskirts|vicinity|edge)\s+of\s+)/i;
// MUST mirror backend dedupe._FILLER — the admin-unit words landed on the
// 2026-07-29 evidence (Nineveh Province/Governorate/Plain = three cards for
// one situation); "city" and "airport" stay because they genuinely
// distinguish places.
const FILLER = new Set(['al', 'el', 'the', 'of', 'in', 'at', 'base', 'airbase',
  'camp', 'province', 'governorate', 'district', 'region', 'area', 'plain']);

/** How far apart two reports may be and still be linked by the LOSSY key below.
 *  Wider than NEAR_M because these are the same settlement under two spellings,
 *  whose geocodes land a few km apart — but far tighter than "anywhere in the
 *  country", which is what makes a consonant skeleton safe to use at all. */
export const FUZZY_NEAR_M = 25_000;

/** A deliberately LOSSY consonant skeleton, used ONLY to link two spellings of
 *  one place. Never for identity, and never sent to the backend.
 *
 *  Arabic and Farsi place names transliterate inconsistently — "Jazan" and
 *  "Jizan" are the same Saudi city, and normalizePlace cannot bridge them
 *  because it only strips accents, qualifiers and filler. One refinery fire
 *  therefore arrived as five separate cards. Dropping vowels folds the whole
 *  family (Jazan/Jizan, Qom/Qum, Kerman/Kirman) onto one key.
 *
 *  Three things keep it from over-merging. It is paired with a DISTANCE GUARD at
 *  the call site, so two unrelated places that happen to share a skeleton stay
 *  apart; it requires three consonants, so short names cannot collide; and it
 *  records whether the name STARTS with a vowel.
 *
 *  That last bit is not a guess — it came from auditing every link this key
 *  introduces across the live corpus. 64 pairs, 63 of them right (Esfahan /
 *  Isfahan, Hodeidah / Hudaydah, seven spellings of Muwaffaq Salti) and exactly
 *  one wrong: ZARQA and AZRAQ, two different Jordanian places that both
 *  skeletonise to "zrq". The distance guard could not save that one because a
 *  bad geocode had already put them at the same point. Keeping the initial-vowel
 *  distinction blocks it while preserving all 63 — checked pair by pair, since
 *  anchoring on the first LETTER instead would have broken Esfahan/Isfahan.
 *
 *  Even so the failure mode is cheap and recoverable: an over-merge here hides
 *  nothing — expand the card and every report is intact — where the backend's
 *  normalize_place feeds entity identity and an over-merge there rewrites
 *  version history. That asymmetry is why this lives on the client only.
 */
export function translitKey(name: unknown): string {
  const base = normalizePlace(name);
  if (!base) return '';
  const skeleton = base
    .replace(/[aeiouy]/g, '')     // the vowels are what vary
    .replace(/(.)\1+/g, '$1')     // "Abdullah"/"Abdulah" -> one l
    .replace(/[^a-z0-9]/g, '');
  if (skeleton.length < 3) return '';
  return (/^[aeiouy]/.test(base) ? 'v' : 'c') + skeleton;
}

export function normalizePlace(name: unknown): string {
  let text = String(name ?? '').trim().toLowerCase();
  if (!text) return '';
  text = text.normalize('NFKD').replace(/\p{M}/gu, '');
  text = text.replace(/air[- ]base/g, 'airbase');
  for (;;) {
    const stripped = text.replace(QUALIFIER_RE, '');
    if (stripped === text) break;
    text = stripped;
  }
  const tokens = text.split(/[^a-z0-9]+/).filter(Boolean);
  const kept = tokens.filter((t) => !FILLER.has(t));
  return (kept.length ? kept : tokens).join(' ');
}

// --- per-event accessors ----------------------------------------------------

// Both of these come from ./when, and that is the entire point: this module used
// to roll its own preference order (postedAt else validFrom) while panels.ts
// rendered validFrom, so a card was GROUPED and SORTED on one clock and
// TIMESTAMPED on another — which is why a header could read older than every
// child inside it, and why rows within an expanded card appeared out of order.
const startOf = (e: EntityDto): number => whenOf(e).at;
const endOf = (e: EntityDto): number => lastReportOf(e);

function pointOf(e: EntityDto): [number, number] | null {
  return e.geometry?.type === 'Point'
    ? (e.geometry.coordinates as [number, number]) : null;
}

function distanceM(a: [number, number], b: [number, number]): number {
  const rad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * rad;
  const dLon = (b[0] - a[0]) * rad;
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin(dLon / 2) ** 2;
  return 6371_000 * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

/** Where clicking a situation card should take the map.
 *
 *  A situation's members scatter: geocode wobble puts one strike's reports a
 *  few km apart, and a country-centroid (vague) member can sit hundreds of km
 *  from the action. So: consider only PRECISE members when any exist, fold
 *  their points onto ~2 km grid cells, take the busiest cell (most members —
 *  the "most frequent" location; recency breaks ties), and inside it return
 *  the most recent member's exact point. A situation with only vague members
 *  still answers — the country centroid is a worse anchor than none at the
 *  card level, but a better one than a map that refuses to move. */
export function situationAnchor(events: EntityDto[]): [number, number] | null {
  const CELL = 0.02; // ~2 km of degrees — the same scale geocode wobble spans
  const precise = events.filter((e) => !e.attrs.vague && pointOf(e));
  const pool = precise.length ? precise : events.filter((e) => pointOf(e));
  if (!pool.length) return null;

  const cells = new Map<string, EntityDto[]>();
  for (const e of pool) {
    const [lon, lat] = pointOf(e)!;
    const key = `${Math.round(lon / CELL)}:${Math.round(lat / CELL)}`;
    const cell = cells.get(key);
    if (cell) cell.push(e); else cells.set(key, [e]);
  }
  let best: EntityDto[] = [];
  let bestAt = -Infinity;
  for (const members of cells.values()) {
    const newest = Math.max(...members.map((e) => whenOf(e).at || 0));
    if (members.length > best.length
        || (members.length === best.length && newest > bestAt)) {
      best = members;
      bestAt = newest;
    }
  }
  const pick = best.reduce((a, b) =>
    ((whenOf(a).at || 0) >= (whenOf(b).at || 0) ? a : b));
  return pointOf(pick);
}

// --- the situation's current state -------------------------------------------
// A situation card is a LIVING OBJECT, not a container of reports: the row
// itself must carry the current picture, and expansion is provenance audit.
// These three functions are that picture. All pure, all client-side — the
// same reasoning that keeps grouping here: a wrong pick hides nothing, the
// member events ride along intact.

/** The best-ESTABLISHED claim among the members — the card's headline.
 *
 *  Deliberately not the newest: the newest report is often the weakest
 *  restatement ("explosions reported"), while the second report named the
 *  facility and the damage. Preference order: a specific kind beats
 *  `unclear`, then extractor confidence, then the more informative (longer)
 *  summary, then recency. As better reports arrive the headline upgrades in
 *  place — which is what makes expansion optional. */
export function headlineOf(events: EntityDto[]): EntityDto {
  const score = (e: EntityDto): [number, number, number, number] => [
    String(e.attrs.kind ?? 'unclear') === 'unclear' ? 0 : 1,
    Number(e.attrs.confidence ?? 0) || 0,
    String(e.attrs.summary ?? '').length,
    whenOf(e).at || 0,
  ];
  return events.reduce((a, b) => {
    const sa = score(a);
    const sb = score(b);
    for (let i = 0; i < sa.length; i += 1) {
      if (sb[i]! > sa[i]!) return b;
      if (sb[i]! < sa[i]!) return a;
    }
    return a; // full tie: keep the earlier pick (events arrive newest first)
  });
}

/** How much newer the newest member must be, when its kind matches the
 *  headline's, before a same-kind report counts as a development rather than
 *  a restatement. */
const LATEST_MIN_GAP_MS = 30 * 60_000;

/** The newest wrinkle — the member behind the card's "⤷ latest" line, or null
 *  when the newest report adds nothing over the headline. A different kind is
 *  always a development (impact → air defences active is news); the same kind
 *  counts only when it is materially newer AND worded as a different claim,
 *  so a corroborating restatement never renders a second line. */
export function latestDevelopment(events: EntityDto[]): EntityDto | null {
  const newest = events[0];
  if (!newest) return null;
  const head = headlineOf(events);
  if (newest === head) return null;
  if (String(newest.attrs.kind ?? '') !== String(head.attrs.kind ?? '')) {
    return newest;
  }
  const gap = (whenOf(newest).at || 0) - (whenOf(head).at || 0);
  const a = String(newest.attrs.summary ?? '').trim().toLowerCase();
  const b = String(head.attrs.summary ?? '').trim().toLowerCase();
  return gap > LATEST_MIN_GAP_MS && a !== '' && a !== b ? newest : null;
}

/** Distinct STATED attributions across the members, when they disagree —
 *  [] otherwise. Stated only: two different model inferences are two guesses,
 *  but two channels STATING different actors is disagreement between sources,
 *  which is signal the reader must see rather than whichever report happened
 *  to render. Display casing follows each name's first appearance. */
export function attributionConflict(events: EntityDto[]): string[] {
  const stated = new Map<string, string>();
  for (const e of events) {
    const name = String(e.attrs.attributedTo ?? '').trim();
    if (!name || String(e.attrs.attributedToBasis ?? '') !== 'stated') continue;
    const key = name.toLowerCase();
    if (!stated.has(key)) stated.set(key, name);
  }
  return stated.size >= 2 ? [...stated.values()] : [];
}

// --- grouping ----------------------------------------------------------------

class UnionFind {
  private parent: number[];
  constructor(n: number) { this.parent = Array.from({ length: n }, (_, i) => i); }
  find(i: number): number {
    while (this.parent[i]! !== i) {
      this.parent[i] = this.parent[this.parent[i]!]!;
      i = this.parent[i]!;
    }
    return i;
  }
  union(a: number, b: number): void { this.parent[this.find(a)] = this.find(b); }
}

export function groupSituations(events: EntityDto[],
                                gapMs: number = SESSION_GAP_MS): Situation[] {
  const uf = new UnionFind(events.length);

  // Link 1 + 2: shared attached place entity, or shared normalized name.
  // The name key includes the country so Tripoli (Lebanon) and Tripoli
  // (Libya) never fuse — mirroring same_incident()'s collision guard.
  const byKey = new Map<string, number>();
  const link = (key: string, i: number) => {
    const first = byKey.get(key);
    if (first === undefined) byKey.set(key, i);
    else uf.union(first, i);
  };
  events.forEach((e, i) => {
    const placeId = String(e.attrs.place_id ?? '');
    if (placeId) link(`place:${placeId}`, i);
    const name = normalizePlace(e.attrs.place);
    if (name) {
      const country = String(e.attrs.country ?? '').trim().toLowerCase();
      link(`name:${name}|${country}`, i);
    }
  });

  // EVERY PER-EVENT NAME FACT IS COMPUTED ONCE, up front. normalizePlace runs
  // a Unicode normalisation and several regexes, and the pairwise passes below
  // used to call it (through translitKey) inside their inner loops — on the
  // order of n² times. Measured before this: 6 s for 2,000 events, 19 s for
  // 4,000, on every feed repaint. The links themselves are unchanged; only
  // where the work happens moved.
  const names = events.map((e) => normalizePlace(e.attrs.place));
  const keys = events.map((e) => translitKey(e.attrs.place));
  const countries = events.map((e) => String(e.attrs.country ?? '').trim().toLowerCase());
  const countryNames = countries.map((c) => normalizePlace(c));

  // Link 2b: the same place under two spellings. Bucketed by consonant skeleton
  // + country, then confirmed pairwise by distance — the guard is what makes a
  // lossy key safe, and without it "Jazan"/"Jizan" would link to anything else
  // that happens to skeletonise the same way anywhere in the country. Buckets
  // are tiny, so the pairwise walk inside them is cheap.
  const fuzzy = new Map<string, number[]>();
  events.forEach((_e, i) => {
    const key = keys[i]!;
    if (!key) return;
    const bucket = `${key}|${countries[i]}`;
    (fuzzy.get(bucket) ?? fuzzy.set(bucket, []).get(bucket)!).push(i);
  });
  for (const members of fuzzy.values()) {
    if (members.length < 2) continue;
    for (let a = 0; a < members.length; a++) {
      for (let b = a + 1; b < members.length; b++) {
        const i = members[a]!;
        const j = members[b]!;
        if (uf.find(i) === uf.find(j)) continue;
        const pi = pointOf(events[i]!);
        const pj = pointOf(events[j]!);
        // No point on either side means nothing to refute the match with.
        if (pi && pj && distanceM(pi, pj) > FUZZY_NEAR_M) continue;
        uf.union(i, j);
      }
    }
  }

  // Name facts for links 2c and 3 — mirrors the server matcher's Sig
  // (app/incidents/matcher.py), which is the point: this module is the
  // fallback for unrouted reports, and the two sides must reach the same
  // grouping or the feed's cards depend on which side happened to group them.
  const tokenSets = names.map((n) => new Set(n.split(' ').filter(Boolean)));
  const isSubset = (a: Set<string>, b: Set<string>): boolean => {
    if (a.size >= b.size) return false;
    for (const t of a) if (!b.has(t)) return false;
    return true;
  };
  const countriesConflict = (i: number, j: number): boolean =>
    Boolean(countries[i] && countries[j] && countries[i] !== countries[j]);

  // Link 2c: one name CONTAINED in the other. "Abqaiq" and "Abqaiq ARAMCO
  // refinery" gazetteer 734 km apart, so no distance rule can join them — the
  // token relation is the evidence, deliberately unguarded by distance. A name
  // that is just the country ("explosion in Iraq") may not be the small side,
  // or every country-wide report folds into whichever named card came first.
  //
  // Indexed by token rather than walked pairwise: a superset of i's tokens must
  // contain every one of them, so the posting list of i's RAREST token holds
  // every candidate and the check inside is the same isSubset as before.
  const byToken = new Map<string, number[]>();
  for (let j = 0; j < events.length; j++) {
    if (!names[j]) continue;
    for (const t of tokenSets[j]!) {
      const list = byToken.get(t);
      if (list) list.push(j); else byToken.set(t, [j]);
    }
  }
  for (let i = 0; i < events.length; i++) {
    if (!names[i] || names[i] === countryNames[i]) continue;
    let candidates: number[] | null = null;
    for (const t of tokenSets[i]!) {
      const list = byToken.get(t);
      if (!list) { candidates = []; break; }
      if (!candidates || list.length < candidates.length) candidates = list;
    }
    if (!candidates) continue;
    for (const j of candidates) {
      if (i === j || !names[j] || countriesConflict(i, j)) continue;
      if (uf.find(i) === uf.find(j)) continue;
      if (isSubset(tokenSets[i]!, tokenSets[j]!)) uf.union(i, j);
    }
  }

  // Whether two reports' own names could describe one object — the veto for
  // link 3. "Jazan Port" and "Jazan Refinery" are 1.5 km apart and are
  // different facilities: when both sides name something compound and the
  // names share no relation, proximity may not overrule them. Single-word
  // toponyms ("Suza"/"Sooza") stay linkable — spelling wobble with a skeleton
  // too short for translitKey is exactly what proximity linking exists for.
  const namesCompatible = (i: number, j: number): boolean => {
    if (!names[i] || !names[j]) return true;
    if (tokenSets[i]!.size < 2 || tokenSets[j]!.size < 2) return true;
    if (names[i] === names[j]) return true;
    if (isSubset(tokenSets[i]!, tokenSets[j]!) || isSubset(tokenSets[j]!, tokenSets[i]!)) return true;
    const ti = keys[i]!;
    return Boolean(ti && ti === keys[j]);
  };

  // Link 3: pinpoint reports within NEAR_M. Vague events sit at region
  // centroids — proximity there is a geocoding artefact, not evidence — so
  // they only ever group by place identity or name.
  //
  // Bucketed on a degree grid so each point is checked against its
  // neighbourhood, not against every other point. The probe reach is
  // conservative — one cell of latitude, and enough cells of longitude for
  // NEAR_M at the widest latitude inside that reach — so no pair the pairwise
  // walk would have found is missed; the distance test itself is unchanged.
  const points = events.map((e) => (e.attrs.vague ? null : pointOf(e)));
  const CELL_DEG = 0.25;
  const CELLS_AROUND = Math.round(360 / CELL_DEG);
  const NEAR_DEG = (NEAR_M / 6371_000) * (180 / Math.PI);
  const cellX = (lon: number): number => {
    const c = Math.floor(lon / CELL_DEG) % CELLS_AROUND;
    return c < 0 ? c + CELLS_AROUND : c;          // the antimeridian wraps
  };
  const cellY = (lat: number): number => Math.floor(lat / CELL_DEG);
  const grid = new Map<string, number[]>();
  for (let i = 0; i < events.length; i++) {
    const p = points[i];
    if (!p) continue;
    const key = `${cellX(p[0])}:${cellY(p[1])}`;
    const list = grid.get(key);
    if (list) list.push(i); else grid.set(key, [i]);
  }
  const dyReach = Math.ceil(NEAR_DEG / CELL_DEG);
  for (let i = 0; i < events.length; i++) {
    const p = points[i];
    if (!p) continue;
    const cx = cellX(p[0]);
    const cy = cellY(p[1]);
    // Longitude degrees per NEAR_M shrink with cos(lat); size the reach for
    // the highest latitude a neighbour within NEAR_M could sit at.
    const worstLat = Math.min(89, Math.abs(p[1]) + NEAR_DEG);
    const dxReach = Math.ceil((NEAR_DEG * 1.1) / Math.cos((worstLat * Math.PI) / 180) / CELL_DEG);
    const dxSpan = Math.min(dxReach, Math.floor(CELLS_AROUND / 2));
    for (let dy = -dyReach; dy <= dyReach; dy++) {
      for (let dx = -dxSpan; dx <= dxSpan; dx++) {
        let x = (cx + dx) % CELLS_AROUND;
        if (x < 0) x += CELLS_AROUND;
        const list = grid.get(`${x}:${cy + dy}`);
        if (!list) continue;
        for (const j of list) {
          if (j <= i || uf.find(i) === uf.find(j)) continue;
          if (!namesCompatible(i, j)) continue;
          if (distanceM(p, points[j]!) <= NEAR_M) uf.union(i, j);
        }
      }
    }
  }

  const components = new Map<number, EntityDto[]>();
  events.forEach((e, i) => {
    const root = uf.find(i);
    (components.get(root) ?? components.set(root, []).get(root)!).push(e);
  });

  // Sessionize each component: chronological walk, split on a quiet gap.
  // The running end extends with each event's last merged report, so a
  // long-running event keeps its situation open.
  const situations: Situation[] = [];
  for (const members of components.values()) {
    const ordered = [...members].sort((a, b) => startOf(a) - startOf(b));
    let session: EntityDto[] = [];
    let sessionEnd = -Infinity;
    const flush = () => {
      if (session.length) situations.push(build(session));
      session = [];
    };
    for (const e of ordered) {
      if (session.length && startOf(e) - sessionEnd > gapMs) flush();
      session.push(e);
      sessionEnd = Math.max(sessionEnd, endOf(e));
    }
    flush();
  }
  return situations.sort((a, b) => b.lastAt - a.lastAt);
}

function build(chronological: EntityDto[]): Situation {
  const kinds: string[] = [];
  const channels = new Set<string>();
  let reportCount = 0;
  for (const e of chronological) {
    const kind = String(e.attrs.kind ?? 'unclear');
    if (!kinds.includes(kind)) kinds.push(kind);
    const sources = e.attrs.sources;
    if (Array.isArray(sources) && sources.length) {
      reportCount += sources.length;
      for (const s of sources) {
        const c = String((s as { channel?: unknown }).channel ?? '');
        if (c) channels.add(c);
      }
    } else {
      reportCount += Number(e.attrs.sourceCount ?? 0) || 1;
      const c = String(e.attrs.channel ?? '');
      if (c) channels.add(c);
    }
  }
  // Header name: an attached place entity ("Ali Al Salem Air Base") is steadier
  // than per-report spellings; latest report's wording otherwise.
  //
  // BUT NOT WHEN THE ATTACHMENT WAS INFERRED. `match_basis` means nothing was
  // mapped at the reported point and the backend associated the event with the
  // most likely site OF THAT KIND — a maritime strike with the nearest naval
  // base, up to 50 km away. Letting that win the header made a card about the
  // Strait of Hormuz read "Naval base", naming somewhere 37.8 km from anything
  // the source said. The anchor is still shown, as an anchor, in the claim line.
  const newestFirst = [...chronological].reverse();
  const named = newestFirst.find((e) => e.attrs.place_name && !e.attrs.match_basis);
  const latest = newestFirst[0]!;   // sessions are never flushed empty
  const place = String(named?.attrs.place_name
    ?? latest.attrs.place ?? latest.name ?? '(unknown)');
  return {
    key: `sit:${chronological[0]!.id}`, place,
    events: newestFirst,
    firstAt: startOf(chronological[0]!),
    lastAt: Math.max(...chronological.map(endOf)),
    kinds, channels: [...channels], reportCount,
  };
}
